/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema';
import { IconButton } from '@atlaskit/button/new';
import type { EditorAppearance } from '@atlaskit/editor-common/types';
import { token } from '@atlaskit/tokens';
import { PromptScreen } from '@atlassian/generative-ai-modal/screens/Prompt';
import type { PromptEditorProps } from '@atlassian/generative-ai-modal/screens/UserInputCommandPalette';

import { RovoPromptScreenWithLogic } from '../../../ExperienceApplication/screens-with-logic/RovoPromptScreenWithLogic';

import { ActionButtons } from './ActionButtons';
import {
	renderEyeOpenIcon,
	renderEyeOpenStrikethroughIcon,
	renderRedoIcon,
	renderUndoIcon,
} from './icons';
import { messages } from './messages';

// The embedded prompt renders frameless, which zeroes the padding the standard
// prompt surface applies. Re-applying it here keeps the PSR input height in line
// with the standard prompt.
const askRovoCSS = css({
	flex: 1,
	minWidth: 0,
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	paddingInline: token('space.200'),
	paddingBlock: token('space.150'),
});

const promptSlotCSS = css({
	flex: 1,
	minWidth: 0,
});

const rightActionsCSS = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
	flexShrink: 0,
});

export interface AskRovoProps {
	appearance: EditorAppearance;
	cancel: (_options?: {
		shouldClearSelectionContext?: boolean;
		shouldIgnoreNextSpaceTrigger?: boolean;
	}) => void;
	canGoNext: boolean;
	canGoPrevious: boolean;
	canRedo: boolean;
	canUndo: boolean;
	canViewChanges: boolean;
	hasFallbackPrompt: boolean;
	hasRovoPrompt: boolean;
	isAIFC: boolean | undefined;
	isDisplayingChanges: boolean;
	isStepThroughExpanded: boolean;
	onNext: () => void;
	onPrevious: () => void;
	onRedo: () => void;
	onTextChange: (input: string) => void;
	onToggleStepThrough: () => void;
	onUndo: () => void;
	onViewChanges: () => void;
	openInRovoChat: boolean | undefined;
	openInRovoChatConfig: { onBeforeSubmit: (prompt: DocNode) => boolean };
	PromptEditor: ((props: PromptEditorProps) => JSX.Element) | undefined;
	segmentIndex: number;
	submit: () => void;
	textInput: string;
	totalSegments: number;
}

/**
 * The prompt input row for the Post Stream Review screen. Embeds the Rovo (or
 * fallback) prompt and hosts the right-hand controls — step-through navigation,
 * the show/hide changes toggle and Undo/Redo — inline alongside it.
 */
export function AskRovo({
	hasRovoPrompt,
	hasFallbackPrompt,
	openInRovoChat,
	openInRovoChatConfig,
	cancel,
	PromptEditor,
	textInput,
	onTextChange,
	submit,
	appearance,
	isAIFC,
	canUndo,
	canRedo,
	onUndo,
	onRedo,
	canViewChanges,
	isDisplayingChanges,
	onViewChanges,
	totalSegments,
	segmentIndex,
	canGoPrevious,
	canGoNext,
	onPrevious,
	onNext,
	isStepThroughExpanded,
	onToggleStepThrough,
}: AskRovoProps): JSX.Element {
	const intl = useIntl();

	// Render the Rovo prompt unless we have a fallback editor to show.
	const shouldRenderFallbackPrompt =
		!(openInRovoChat && hasRovoPrompt) && hasFallbackPrompt && !!PromptEditor;

	return (
		<div css={askRovoCSS} data-testid="post-stream-review-ask-rovo">
			<div css={promptSlotCSS}>
				{shouldRenderFallbackPrompt && PromptEditor ? (
					<PromptScreen
						textInput={textInput}
						onTextChange={onTextChange}
						submit={submit}
						cancel={cancel}
						PromptEditor={PromptEditor}
						appearance={appearance}
						isAIFC={isAIFC}
					/>
				) : (
					<RovoPromptScreenWithLogic
						cancel={cancel}
						openInRovoChatConfig={
							openInRovoChat && hasRovoPrompt ? openInRovoChatConfig : undefined
						}
						autoClose={false}
						isPostStreamReview={true}
						rovoInlineChatAppearance="frameless"
					/>
				)}
			</div>
			<div css={rightActionsCSS}>
				<ActionButtons
					totalSegments={totalSegments}
					segmentIndex={segmentIndex}
					canGoPrevious={canGoPrevious}
					canGoNext={canGoNext}
					isStepThroughExpanded={isStepThroughExpanded}
					onPrevious={onPrevious}
					onNext={onNext}
					onToggleStepThrough={onToggleStepThrough}
				/>
				<IconButton
					appearance="subtle"
					icon={isDisplayingChanges ? renderEyeOpenStrikethroughIcon : renderEyeOpenIcon}
					label={intl.formatMessage(
						isDisplayingChanges ? messages.hideChanges : messages.viewChanges,
					)}
					isSelected={isDisplayingChanges}
					onClick={onViewChanges}
					isDisabled={!canViewChanges}
					testId="post-stream-review-view-changes"
				/>
				<IconButton
					appearance="subtle"
					icon={renderUndoIcon}
					label={intl.formatMessage(messages.undo)}
					isDisabled={!canUndo}
					onClick={onUndo}
					testId="post-stream-review-undo"
				/>
				<IconButton
					appearance="subtle"
					icon={renderRedoIcon}
					label={intl.formatMessage(messages.redo)}
					isDisabled={!canRedo}
					onClick={onRedo}
					testId="post-stream-review-redo"
				/>
			</div>
		</div>
	);
}
